// src/seo/internalLinks.ts  (או אצלך: src/utils/internalLinks.ts)
// i18n-aware internal links builder

import {
  type SeoCity,
  REGIONS,
  REGION_CITIES,
  EVENT_TYPES,
  type Region,
  type EventType,
  globalTypePath,
  typeLocationPath,
} from '@/constants/geo';

import { type SeoService, serviceByType } from '@/constants/core';

export type InternalLink = { label: string; href: string; title?: string };

const GLOBAL_SERVICE_FOOTER_CITIES_BY_REGION: Readonly<Record<Region, readonly SeoCity[]>> = {
  דרום: ['אשדוד', 'אשקלון', 'באר שבע'],
  מרכז: [
    'ירושלים',
    'נס ציונה',
    'מודיעין',
    'נתניה',
    'עמק חפר',
    'פתח תקווה',
    'ראשון לציון',
    'תל אביב',
    'כפר סבא',
    'רחובות',
    'אור יהודה',
  ],
  צפון: ['חדרה', 'עמק יזרעאל', 'חיפה', 'נוף הגליל'],
};

const MAIN_SERVICE_PARENT_TYPES: readonly EventType[] = [
  'חתונה',
  'חינה',
  'בר-מצווה',
  'אירוע',
];

// ---------------------
// i18n contract (minimal) — FIXED
// ---------------------
export type InternalLinksI18n = {
  // אצלך זה string, אז מקבלים string ומנרמלים בפנים
  HTML_LANG: string;

  preferredKeywordForService: (service: SeoService) => string;

  translateCity: (city: SeoCity | string) => string;
  translateRegion: (region: Region | string) => string;
};

function isHe(i18n: InternalLinksI18n) {
  return String(i18n.HTML_LANG).toLowerCase().startsWith('he');
}

// ---------------------
// URL helpers
// ---------------------
function relUrlWithSlash(pathOrUrl: string): string {
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl.endsWith('/') ? pathOrUrl : `${pathOrUrl}/`;
  const clean = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
  return clean.endsWith('/') ? clean : `${clean}/`;
}

function notSelf(href: string, selfPath: string) {
  const norm = (u: string) => u.replace(/\/+$/, '/');
  return norm(href) !== norm(selfPath);
}

function uniqueByHref(list: InternalLink[]): InternalLink[] {
  const seen = new Set<string>();
  return list.filter((l) => {
    if (seen.has(l.href)) return false;
    seen.add(l.href);
    return true;
  });
}

function clamp<T>(arr: ReadonlyArray<T>, n: number): T[] {
  return n >= 0 ? arr.slice(0, n) : arr.slice();
}

// ---------------------
// Keyword helpers
// ---------------------
function primaryKeyword(i18n: InternalLinksI18n, service: SeoService): string {
  return i18n.preferredKeywordForService(service) || service;
}

// ---------------------
// UI helpers (labels)
// ---------------------
function inLoc(i18n: InternalLinksI18n, name: string): string {
  const n = String(name ?? '').trim();
  if (!n) return '';
  return isHe(i18n) ? `ב${n}` : `in ${n}`;
}

// ---------------------
// Canonical href builders (SSOT paths, Hebrew slugs)
// ---------------------
function globalTypeHref(type: EventType): string {
  return relUrlWithSlash(globalTypePath(type));
}

function cityTypeHref(type: EventType, city: SeoCity): string {
  return relUrlWithSlash(typeLocationPath(type, String(city)));
}

function regionTypeHref(type: EventType, region: Region): string {
  return relUrlWithSlash(typeLocationPath(type, region));
}

function cityHubHref(city: SeoCity): string {
  return relUrlWithSlash(typeLocationPath('אירוע', String(city)));
}

function regionHubHref(region: Region): string {
  return relUrlWithSlash(typeLocationPath('אירוע', region));
}

// ---------------------
// Builders
// ---------------------
export function buildMainServiceParentLinks(i18n: InternalLinksI18n): InternalLink[] {
  return MAIN_SERVICE_PARENT_TYPES.map((type) => {
    const keyword = primaryKeyword(i18n, serviceByType[type]);

    return {
      label: keyword,
      href: globalTypeHref(type),
      title: isHe(i18n) ? `${keyword} לכל הארץ` : `${keyword} in Israel`,
    };
  });
}

export function buildInternalLinksForTarget(
  i18n: InternalLinksI18n,
  opts: {
    region: Region;
    city: SeoCity;
    service: SeoService;
    type: EventType;
    maxS4?: number;
    maxFooter?: number;
    includeRegionTarget?: boolean;
  }
) {
  const {
    region,
    city,
    service,
    type,
    maxS4 = 6,
    maxFooter = 30,
    includeRegionTarget = true,
  } = opts;

  const currentPrimary = primaryKeyword(i18n, service);

  const selfPath = cityTypeHref(type, city);

  const cityUi = i18n.translateCity(city);
  const regionUi = i18n.translateRegion(region);

  const keywordForType = i18n.preferredKeywordForService(serviceByType[type]);

  const section5Candidates: InternalLink[] = [
    {
      href: globalTypeHref(type),
      label: keywordForType,
      title: isHe(i18n) ? `${keywordForType} לכל הארץ` : `${keywordForType} in Israel`,
    },
    ...(includeRegionTarget
      ? [
          {
            href: regionTypeHref(type, region),
            label: `${keywordForType} ${inLoc(i18n, regionUi)}`,
            title: `${keywordForType} ${inLoc(i18n, regionUi)}`,
          },
        ]
      : []),
  ].filter((l) => notSelf(l.href, selfPath));

  const section6Candidates: InternalLink[] = [
    {
      href: regionHubHref(region),
      label: isHe(i18n) ? `דיג׳יי ב${regionUi}` : `DJ in ${regionUi}`,
      title: isHe(i18n) ? `דיג׳יי ב${regionUi}` : `DJ in ${regionUi}`,
    },
    {
      href: cityHubHref(city),
      label: isHe(i18n) ? `דיג׳יי ב${cityUi}` : `DJ in ${cityUi}`,
      title: isHe(i18n) ? `דיג׳יי ב${cityUi}` : `DJ in ${cityUi}`,
    },
  ].filter((l) => notSelf(l.href, selfPath));

  const s4Candidates: InternalLink[] = EVENT_TYPES
    .filter((t) => t !== type)
    .map((t) => {
      const href = cityTypeHref(t, city);
      const kw = primaryKeyword(i18n, serviceByType[t]);
      const label = `${kw} ${inLoc(i18n, cityUi)}`;
      return { label, title: label, href };
    })
    .filter((l) => notSelf(l.href, selfPath));

  const regionCities = (REGION_CITIES[region] || []).filter((c) => c !== city);
  const footerCandidates: InternalLink[] = regionCities.map((c) => {
    const href = cityTypeHref(type, c as SeoCity);
    const cUi = i18n.translateCity(c as SeoCity);
    const label = `${currentPrimary} ${inLoc(i18n, cUi)}`;
    return { label, title: label, href };
  });

  const section4 = clamp(uniqueByHref(s4Candidates), maxS4);

  const used = new Set([
    ...section4.map((l) => l.href),
    ...section5Candidates.map((l) => l.href),
    ...section6Candidates.map((l) => l.href),
  ]);

  const footer = clamp(
    uniqueByHref(footerCandidates.filter((l) => !used.has(l.href))),
    maxFooter
  );

  return {
    section4,
    footer,
    section5: uniqueByHref(section5Candidates),
    section6: uniqueByHref(section6Candidates),
  };
}

export function buildGlobalServiceCityFooterLinks(
  i18n: InternalLinksI18n,
  opts: {
    service: SeoService;
    type: EventType;
  }
): InternalLink[] {
  const keyword = primaryKeyword(i18n, opts.service);

  return uniqueByHref(
    REGIONS.flatMap((region) =>
      GLOBAL_SERVICE_FOOTER_CITIES_BY_REGION[region].map((city) => {
        const cityUi = i18n.translateCity(city);
        const label = `${keyword} ${inLoc(i18n, cityUi)}`;

        return {
          label,
          title: label,
          href: cityTypeHref(opts.type, city),
        };
      })
    )
  );
}
